// front-end/src/components/block-system/block-manager/block-editor.js

import {getBlockCreator, getBaseType, getAvailableBlockTypes, blockCreators, getBlockDefinition} from '../block-configs';
import {showErrorNotification, showSuccessNotification} from '../../../utils/notifications';
import {isAuthenticated} from '../../auth';
import {createAndTrackBlock, loadContentForBlocks, initCarousel} from './block-renderer';
import {checkAuthForBlockOperations} from './block-api';
import {
    getNextBlockNumber,
    addPendingBlock,
    removePendingBlock,
    addPendingDeleteBlock
} from './block-state';
import {makeBlockDraggable} from './block-sortable';

export function addDeleteButton(blockElement, blockId) {
    if (!blockElement) return;

    if (blockElement.querySelector('.block-delete-button')) return;

    const deleteButton = document.createElement('button');
    deleteButton.className = 'block-delete-button';
    deleteButton.innerHTML = '&#10006;';
    deleteButton.title = 'Delete block';
    deleteButton.style.cssText = `
    position: absolute;
    top: 10px;
    right: 40px;
    background-color: #ff4d4d;
    color: white;
    border: none;
    border-radius: 4px;
    width: 30px;
    height: 30px;
    cursor: pointer;
    font-size: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    opacity: 0.8;
    transition: opacity 0.3s;
  `;

    deleteButton.addEventListener('mouseover', () => {
        deleteButton.style.opacity = '1';
    });

    deleteButton.addEventListener('mouseout', () => {
        deleteButton.style.opacity = '0.8';
    });

    deleteButton.addEventListener('click', (e) => {
        e.stopPropagation();
        deleteBlock(blockElement, blockId);
    });

    if (getComputedStyle(blockElement).position === 'static') {
        blockElement.style.position = 'relative';
    }

    blockElement.appendChild(deleteButton);
}

export function deleteBlock(blockElement, blockId) {
    if (!checkAuthForBlockOperations()) return;

    const blockName = blockElement.getAttribute('data-block-name');
    const isNewsBlock = blockName && blockName.startsWith('news-');
    const isCourseModule = blockName && blockName.includes('course-module-');
    const newsId = blockElement.getAttribute('data-news-id');

    let confirmMessage = 'Are you sure you want to delete this block? This action cannot be undone.';

    if (isNewsBlock) {
        if (newsId) {
            confirmMessage = 'Are you sure you want to delete this news block? This will also delete the associated news article, page, and all content. This action cannot be undone.';
        } else {
            confirmMessage = 'Are you sure you want to delete this news block? This action cannot be undone.';
        }
    } else if (isCourseModule) {
        confirmMessage = 'Are you sure you want to delete this course module? This action cannot be undone.';
    }

    if (confirm(confirmMessage)) {
        // НОВОЕ: Запоминаем родительский элемент для обновления номеров модулей
        const parentSection = blockElement.parentElement;
        const wasCourseModule = isCourseModule;

        if (blockId) {
            addPendingDeleteBlock(
                blockElement,
                blockId,
                blockElement.style.display || 'block'
            );

            blockElement.style.display = 'none';
            showSuccessNotification('Block will be deleted after publishing changes');
        } else {
            blockElement.remove();
            removePendingBlock(blockName);
            showSuccessNotification('New block deleted');
        }

        // НОВОЕ: Если удалили модуль курса, обновляем номера остальных
        if (wasCourseModule && parentSection) {
            setTimeout(() => {
                updateModuleNumbersAfterDelete(parentSection);
            }, 100);
        }
    }
}

// НОВАЯ ФУНКЦИЯ: Обновление номеров модулей после удаления
function updateModuleNumbersAfterDelete(parentSection) {
    try {
        const {updateAllModuleNumbers} = require('../block-configs/block-types/course-module');
        updateAllModuleNumbers(parentSection);
        console.log('[BlockEditor] Updated module numbers after deletion');
    } catch (error) {
        console.error('[BlockEditor] Error updating module numbers after deletion:', error);
    }
}

function determinePageForNewsContent(sectionElement) {
    const path = window.location.pathname;

    // Clean URL: /case/{slug}
    const casePathMatch = path.match(/^\/case\/([^/]+)/);
    if (casePathMatch) return `Case - ${casePathMatch[1]}`;

    // Clean URL: /news/{slug}
    const newsPathMatch = path.match(/^\/news\/([^/]+)/);
    if (newsPathMatch) return `News - ${newsPathMatch[1]}`;

    // Сторінка деталей діагностики
    const diagPathMatch = path.match(/^\/diagnostic\/([^/]+)/);
    if (diagPathMatch) return `Diagnostic - ${diagPathMatch[1]}`;
    if (path.match(/diagnostic-detail(\.html)?$/)) {
        const urlParams = new URLSearchParams(window.location.search);
        const diagnosticSlug = urlParams.get('slug');
        if (diagnosticSlug) return `Diagnostic - ${diagnosticSlug}`;
    }

    // Проверяем, находимся ли мы на странице новости
    if (path.match(/\/News(\.html)?$/)) {
        const urlParams = new URLSearchParams(window.location.search);
        const newsSlug = urlParams.get('slug');

        if (newsSlug) {
            console.log(`[BlockEditor] Found news slug: ${newsSlug}`);
            const bodyPage = document.body.getAttribute('data-page');
            if (bodyPage && (bodyPage.startsWith('News - ') || bodyPage.startsWith('Case - '))) {
                return bodyPage;
            }
            return newsSlug;
        }
    }

    // Если не на странице новости, ищем ближайший news-block
    let parent = sectionElement.parentElement;
    while (parent) {
        const newsBlock = parent.querySelector('[data-block-name^="news-block-"]');
        if (newsBlock) {
            const newsSlug = newsBlock.getAttribute('data-news-slug');
            if (newsSlug) {
                return newsSlug;
            }
            const blockName = newsBlock.getAttribute('data-block-name');
            return blockName;
        }
        parent = parent.parentElement;
    }

    const bodyPage = document.body.getAttribute('data-page');
    return bodyPage || 'News';
}

function determinePageForCourseModules() {
    // Проверяем, находимся ли мы на странице курса
    if (window.location.pathname.match(/course-detail(\.html)?$/)) {
        const urlParams = new URLSearchParams(window.location.search);
        const courseSlug = urlParams.get('slug');

        if (courseSlug) {
            console.log(`[BlockEditor] Found course slug: ${courseSlug}`);
            return courseSlug;
        }
    }

    // Проверяем data-page у body
    const bodyPage = document.body.getAttribute('data-page');
    if (bodyPage && bodyPage.startsWith('Course - ')) {
        // Извлекаем slug из "Course - course-slug"
        const courseSlug = bodyPage.replace('Course - ', '');
        return courseSlug;
    }

    // Fallback: если ничего не нашли, используем 'course-detail'
    return 'course-detail';
}

// НОВАЯ ФУНКЦИЯ: Генерирует уникальный префикс для блока на основе секции
function generateSectionSpecificBlockName(sectionElement, blockType) {
    const sectionId = sectionElement.id || sectionElement.getAttribute('class');

    // ВАЖНО: Для существующих секций сохраняем старые префиксы
    if (sectionId === 'work-process-accordion') {
        // Только для новой секции используем новый префикс
        return 'work-process-step';
    } else if (sectionId.includes('accordion-cases') || sectionId.includes('accordion-2')) {
        // Для всех существующих аккордеонов сохраняем стандартный префикс
        return 'case';
    } else if (sectionId && sectionId !== 'accordion-cases' && sectionId.includes('accordion')) {
        // Только для будущих новых аккордеонов создаем уникальный префикс
        const cleanPrefix = sectionId.replace(/[^a-zA-Z0-9-]/g, '').toLowerCase();
        return cleanPrefix + '-item';
    }

    // Fallback: возвращаем оригинальный тип блока
    return blockType;
}

export function addNewBlock(sectionElement, blockType) {
    if (!checkAuthForBlockOperations()) return null;

    const sectionId = sectionElement.id || sectionElement.getAttribute('class');
    console.log(`Adding new block of type ${blockType} to section ${sectionId}`);

    const baseType = getBaseType(sectionId);

    const availableBlockTypes = getAvailableBlockTypes(sectionId);

    if (!availableBlockTypes.includes(blockType)) {
        console.error(`Block type ${blockType} is not allowed in section ${sectionId} (base type: ${baseType})`);
        console.log(`Available block types for ${baseType}:`, availableBlockTypes);
        showErrorNotification(`Block type ${blockType} is not allowed in section ${sectionId}`);
        return null;
    }

    // НОВАЯ ЛОГИКА: Проверяем требует ли блок страницу
    const blockDefinition = getBlockDefinition(blockType);
    const requiresPage = blockDefinition && blockDefinition.requiresPage;

    // ИЗМЕНЕНО: Генерируем имя блока с учетом секции
    const blockPrefix = generateSectionSpecificBlockName(sectionElement, blockType);
    const blockNumber = getNextBlockNumber(blockPrefix);
    const blockName = `${blockPrefix}-${blockNumber}`;

    console.log(`Generated block name: ${blockName} (prefix: ${blockPrefix}, number: ${blockNumber})`);

    let maxOrder = -1;
    const existingBlocks = Array.from(sectionElement.querySelectorAll('[data-block-id], [data-block-name]'));

    // СПЕЦИАЛЬНАЯ ЛОГИКА: Для модулей курса используем отдельную нумерацию
    let newOrder;
    if (blockType === 'course-module') {
        // Для модулей курса фильтруем только блоки модулей
        const existingModules = existingBlocks.filter(block =>
            block.getAttribute('data-block-name')?.includes('course-module-')
        );

        if (existingModules.length === 0) {
            newOrder = 0; // Первый модуль
        } else {
            // Находим максимальный order среди модулей
            let maxModuleOrder = -1;
            existingModules.forEach(module => {
                const moduleOrder = parseInt(module.getAttribute('data-order'), 10);
                if (!isNaN(moduleOrder)) {
                    maxModuleOrder = Math.max(maxModuleOrder, moduleOrder);
                }
            });
            newOrder = maxModuleOrder + 1;
        }

        console.log(`[BlockEditor] Course module: existing modules: ${existingModules.length}, newOrder: ${newOrder}`);
    } else {
        // Для остальных блоков - стандартная логика
        existingBlocks.forEach(block => {
            if (block.hasAttribute('data-order')) {
                const orderStr = block.getAttribute('data-order');
                if (orderStr !== null && orderStr !== undefined) {
                    const orderNum = parseInt(orderStr, 10);
                    if (!isNaN(orderNum)) {
                        maxOrder = Math.max(maxOrder, orderNum);
                    }
                }
            }
        });

        newOrder = maxOrder + 1;
    }

    const blockData = {
        class_name: blockType,
        data_block_name: blockName,
        parent_block: sectionId,
        order: newOrder
    };

    // НОВОЕ: Для блоков, которые требуют страницу, определяем и добавляем page_title
    if (requiresPage) {
        let pageTitle = null;

        if (sectionId === 'news-content' || baseType === 'news-content') {
            pageTitle = determinePageForNewsContent(sectionElement);
            console.log(`Determined page for news-content block: ${pageTitle}`);
        } else if (sectionId === 'course-modules-section' || baseType === 'course-modules-section') {
            // НОВОЕ: Для модулей курса определяем страницу
            pageTitle = determinePageForCourseModules();
            console.log(`Determined page for course-modules block: ${pageTitle}`);
        } else if (sectionId === 'diagnostic-content' || baseType === 'diagnostic-content') {
            pageTitle = determinePageForNewsContent(sectionElement);
            console.log(`Determined page for diagnostic-content block: ${pageTitle}`);
        }

        if (pageTitle) {
            // Якщо префікс вже є — використовуємо як є
            if (pageTitle.startsWith('News - ') || pageTitle.startsWith('Case - ') ||
                pageTitle.startsWith('Course - ') || pageTitle.startsWith('Diagnostic - ')) {
                blockData.page_title = pageTitle;
            } else if (pageTitle.startsWith('case-')) {
                blockData.page_title = `Case - ${pageTitle}`;
            } else if (pageTitle.startsWith('course-') || sectionId === 'course-modules-section') {
                blockData.page_title = `Course - ${pageTitle}`;
            } else if (pageTitle.startsWith('diagnostic-')) {
                blockData.page_title = `Diagnostic - ${pageTitle}`;
            } else {
                blockData.page_title = `News - ${pageTitle}`;
            }
            console.log(`Adding block ${blockName} with page: ${blockData.page_title}`);
        } else if (requiresPage) {
            console.error(`Block type ${blockType} requires a page but no page could be determined`);
            showErrorNotification(`Cannot add ${blockType} block: no page context found`);
            return null;
        }
    }

    const createBlock = getBlockCreator(sectionId, blockType);
    if (!createBlock) {
        console.error(`Block creator not found for type: ${blockType} in section ${sectionId} (base type: ${baseType})`);
        console.log(`Available creators:`, blockCreators);
        showErrorNotification(`Block creator not found for type: ${blockType}`);
        return null;
    }

    const createdBlock = createBlock(blockData, sectionElement);
    createdBlock.setAttribute('data-order', String(newOrder));

    console.log(`Created block with order: ${newOrder}`, blockData);

    // Add block to pending
    addPendingBlock(blockData);

    addDeleteButton(createdBlock);
    loadContentForBlocks(createdBlock);

    // Special handling for different block types
    if (blockType === 'news-block') {
        showSuccessNotification(`Added new ${blockType} block - will be created as draft after publishing`);
    } else if (blockType === 'course-module') {
        showSuccessNotification(`Added new course module - remember to publish changes`);
    } else if (requiresPage) {
        showSuccessNotification(`Added new ${blockType} block for page: ${blockData.page_title || 'current'}`);
    } else {
        showSuccessNotification(`Added new ${blockName} block in section ${sectionId}`);
    }

    createdBlock.setAttribute('draggable', 'true');
    makeBlockDraggable(createdBlock);

    document.dispatchEvent(new CustomEvent('blockAdded', {
        detail: { blockElement: createdBlock, blockData }
    }));

    setTimeout(initCarousel, 300);

    return createdBlock;
}